import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";

import FormInput from "../../../components/Common/FormInput/FormInput";
import FormSelect from "../../../components/Common/FormSelect/FormSelect";

import headerService from "../../../services/header-service";
import VendorOnboardingService from "../../../services/VendorOnboarding/vendorOnboarding-service";

const CreateVendor = () => {
  const navigate = useNavigate();
  const [companyList, setCompanyList] = useState([]);
  const [vendorTypeList, setVendorTypeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    contact: "",
    vendorType: "",
    VendorCompany: "",
    company: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCompanies();
    fetchVendorTypes();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await headerService.getKnackCompanyListAPI();
      const ok = res?.data?.statusCode === 200;
      const list = ok ? res?.data?.responseData || [] : [];

      const companyListData = Array.isArray(list)
        ? list.map((c) => ({
            ...c,
            company_Code: String(c?.company_Code ?? "").trim(),
            name: String(c?.name ?? "").trim(),
          }))
        : [];

      setCompanyList(companyListData);

      if (companyListData.length > 0 && !form.company) {
        setForm((prev) => ({
          ...prev,
          company: companyListData[0].company_Code,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      setCompanyList([]);
    }
  };

  const fetchVendorTypes = async () => {
    try {
      const res = await VendorOnboardingService.getTypeOfVendorMaster();
      const ok = res?.data;
      const list = ok ? res?.data || [] : [];

      setVendorTypeList(Array.isArray(list) ? list : []);
    } catch {
      setVendorTypeList([]);
    }
  };

  // --- helpers ---
  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };
  const handleSelect = useCallback((name, value) => {
    setForm((p) => ({ ...p, [name]: value ?? "" }));
    setErrors((p) => ({ ...p, [name]: "" }));
  }, []);

  const handleReset = () => {
    setForm({
      name: "",
      email: "",
      contact: "",
      vendorType: "",
      company: "",
      VendorCompany: "",
    });
    setErrors({});
  };

  const validate = () => {
    const next = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^\d{10}$/;
    const isEmpty = (v) => v == null || String(v).trim() === "";

    if (!form.name.trim()) next.name = "Name is required";
    if (!form.email.trim()) next.email = "Email is required";
    else if (!emailRe.test(form.email)) next.email = "Enter a valid email";

    if (!form.contact.trim()) next.contact = "Contact is required";
    else if (!phoneRe.test(form.contact))
      next.contact = "Contact must be 10 digits";

    if (isEmpty(form.vendorType)) next.vendorType = "Vendor Type is required";

    if (!form.VendorCompany.trim())
      next.VendorCompany = "Vendor Company is required";

    if (isEmpty(form.company)) next.company = "Company selection is required";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = {
        firstName: form.name,
        email: form.email,
        contact: form.contact,
        vendorType: String(form.vendorType),
        companyCode: form.company,
        organisation: form.VendorCompany,
      };

      const res = await VendorOnboardingService.saveVendorRequest(payload);
      if (res?.data?.statusCode === 200) {
        NotificationManager.success(res?.data?.message || "Submitted");
        handleReset();
      } else {
        NotificationManager.error(res?.data?.message || "Submission failed");
      }
    } catch (e) {
      console.error(e);
      const message =
        e?.response?.data?.message || e?.message || "Submission failed";
      NotificationManager.error(message);
    } finally {
      setLoading(false);
    }
  };

  // --- options ---
  const vendorTypeOptions =
    vendorTypeList.map((v) => ({
      label: String(v?.vendorType || "").trim(),
      value: String(v?.id).trim(),
    })) || [];

  const companyOptions =
    companyList.map((c) => ({
      label: c?.name,
      value: c?.company_Code,
    })) || [];

  return (
    <div className="app-content content VendorOnboarding">
      <div className="content-wrapper">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="content-header-title d-inline-block">
            Request Vendor
          </h5>
        </div>

        <div className="card" style={{ minHeight: "90%" }}>
          <div className="card-content">
            <div className="card-body">
              <Row gutter={[16, 16]} className="py-3">
                <Col span={4}>
                  <FormInput
                    name="VendorCompany"
                    placeholder="Vendor Company *"
                    value={form.VendorCompany}
                    onChange={handleInput}
                    error={errors.VendorCompany}
                  />
                </Col>
                <Col span={4}>
                  <FormInput
                    name="name"
                    placeholder="Person Name *"
                    value={form.name}
                    onChange={handleInput}
                    error={errors.name}
                  />
                </Col>
                <Col span={4}>
                  <FormInput
                    name="email"
                    placeholder="Email *"
                    value={form.email}
                    onChange={handleInput}
                    error={errors.email}
                  />
                </Col>
                <Col span={4}>
                  <FormInput
                    name="contact"
                    placeholder="Contact *"
                    value={form.contact}
                    onChange={handleInput}
                    error={errors.contact}
                  />
                </Col>

                <Col span={4}>
                  <FormSelect
                    name="vendorType"
                    allowClear
                    placeholder="Vendor Type *"
                    value={form.vendorType || undefined}
                    onChange={(val) => handleSelect("vendorType", val)}
                    showSearch
                    options={vendorTypeOptions}
                    onClear={() => handleSelect("vendorType", "")}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    error={errors.vendorType}
                  />
                </Col>

                <Col span={4}>
                  <FormSelect
                    name="company"
                    allowClear
                    showSearch
                    placeholder="Select Company *"
                    value={form.company || undefined}
                    onChange={(val) => handleSelect("company", val)}
                    className="w-100"
                    onClear={() => handleSelect("company", "")}
                    options={companyOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    error={errors.company}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>

        <Row className="d-flex justify-content-between">
          <div className="pt-0 reset_save_btn justify-content-start">
            <Button
              className="reset_btn"
              onClick={() => navigate("/VendorManagement")}
            >
              Back
            </Button>
          </div>
          <div className="pt-0 reset_save_btn justify-content-end mb-2">
            <Button className="reset_btn" onClick={handleReset}>
              Reset
            </Button>
            <Button
              className="save_btn"
              loading={loading}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </Row>
      </div>
    </div>
  );
};

export default CreateVendor;
